import isEqual from 'lodash/isEqual';
import { BaseService } from '../BaseService';
import { createObjectsCache, createSingleton } from '../singleton';
import { extractErrorMessage } from '../extractErrorMessage';
import { createDiagnosticLogger } from '../createDiagnosticLogger';
const diagnosticLogger = createDiagnosticLogger('bi:core:services:createService');
function createDep(ServiceClass, subscription, args, onUpdate) {
    let instance;
    if (ServiceClass.getInstance && !args.length) {
        instance = ServiceClass.getInstance();
    }
    else if (ServiceClass.createInstance) {
        instance = ServiceClass.createInstance(...args);
    }
    else {
        instance = new ServiceClass(...args);
    }
    if (subscription) {
        instance.subscribe(subscription, onUpdate);
    }
    else {
        instance.subscribeUpdates(onUpdate);
    }
    return { ServiceClass, subscription, args, instance, onUpdate };
}
function destroyDep(dep) {
    dep.instance.unsubscribe(dep.onUpdate);
    dep.onUpdate = null;
    if (dep.ServiceClass.getInstance && !dep.args.length) {
        //                           
    }
    else {
        dep.instance.release();
    }
    dep.instance = null;
}
const DEFAULT_MODEL = Object.assign([], { error: null, loading: true });
function createService(name, callback) {
    class Service extends BaseService {
        static MODEL;
        _args;
        _deps = [];
        _memberFunction = [];
        constructor(...args) {
            super(DEFAULT_MODEL);
            this._args = args.slice(0);
            this._onDepUpdated();
        }
        _findDep = (ServiceClass, subscription, args) => {
            //                                                 
            const equals = (d) => {
                if (d.ServiceClass !== ServiceClass)
                    return false; //                  
                if (String(d.subscription) !== String(subscription))
                    return false; //                    
                if (!isEqual(d.args, args))
                    return false; //                     
                return true;
            };
            return this._deps.find(equals);
        };
        _loadModel() {
            const usedDeps = [];
            const usedMemberFunctions = {};
            const useServiceItselfWithCustomSubscription = (serviceClass, subscription, ...args) => {
                const dep = this._findDep(serviceClass, subscription, args) || createDep(serviceClass, subscription, args, this._onDepUpdated);
                if (!this._deps.includes(dep))
                    this._deps.push(dep);
                if (!usedDeps.includes(dep))
                    usedDeps.push(dep);
                return dep.instance;
            };
            const useMemberFunction = (memberName, fn) => {
                usedMemberFunctions[memberName] = fn;
            };
            const useServiceWithCustomSubscription = (serviceClass, subscription, ...args) => useServiceItselfWithCustomSubscription(serviceClass, subscription, ...args).getModel();
            const useServiceItself = (serviceClass, ...args) => useServiceItselfWithCustomSubscription(serviceClass, '', ...args);
            const useService = (serviceClass, ...args) => useServiceItself(serviceClass, ...args).getModel();
            useService.useService = useService;
            useService.useServiceItself = useServiceItself;
            useService.useServiceWithCustomSubscription = useServiceWithCustomSubscription;
            useService.useServiceItselfWithCustomSubscription = useServiceItselfWithCustomSubscription;
            useService.useMemberFunction = useMemberFunction;
            const model = callback.call(this, useService, ...this._args);
            //          
            //                                                              
            const then = () => {
                const unusedDeps = this._deps.filter(d => !usedDeps.includes(d));
                this._deps = usedDeps;
                unusedDeps.forEach(destroyDep);
                //                          
                for (const memberFunction of this._memberFunction) { //                                   
                    if (!(memberFunction in usedMemberFunctions)) { //                                            
                        delete this[memberFunction]; //              
                    }
                }
                this._memberFunction = Object.keys(usedMemberFunctions); //                                     
                for (const memberFunction of this._memberFunction) { //              
                    Object.defineProperty(this, memberFunction, {
                        value: usedMemberFunctions[memberFunction],
                        writable: true,
                        enumerable: false,
                        configurable: true, //                                
                    });
                }
            };
            if (model instanceof Promise) {
                model.then(then, then);
            }
            else {
                then();
            }
            return model;
        }
        _isUpdateRunning = false;
        _isUpdateScheduled = false;
        _onDepUpdated = () => {
            //                                
            if (this._isUpdateRunning) {
                this._isUpdateScheduled = true;
                return;
            }
            this._isUpdateRunning = true;
            //                                                                                                   
            //                                                            
            //                                                       
            //                  
            //                                   
            //                                                     
            //                                                              
            const then = (model) => {
                if (model === undefined) {
                    //                                           
                }
                else if (Array.isArray(model)) {
                    this._setModel(model); //                                                                                      
                }
                else {
                    //                          
                    this._setModel(model); //                          
                }
            };
            const fail = (err) => {
                diagnosticLogger.error(err);
                this._updateWithError(extractErrorMessage(err));
            };
            const finish = () => {
                this._isUpdateRunning = false;
                //                                                                        
                //                                                         
                //                                           
                if (this._isUpdateScheduled) {
                    this._isUpdateScheduled = false;
                    this._onDepUpdated();
                }
            };
            try {
                const modelOrPromise = this._loadModel(); //                                 
                if (modelOrPromise instanceof Promise) {
                    Promise.resolve()
                        .then(() => modelOrPromise) //                                            
                        .then(then, fail)
                        .finally(finish);
                    return;
                }
                else {
                    then(modelOrPromise);
                }
            }
            catch (err) {
                fail(err);
            }
            finish();
        };
        _dispose() {
            this._deps.forEach(destroyDep);
            this._deps = [];
            let cache = Service._cache;
            if (cache) {
                cache.remove(...this._args);
            }
            super._dispose();
        }
    }
    if (name) {
        Object.defineProperty(Service, 'name', { value: name, writable: false });
        if (callback.length > 1) { //                         
            const _cache = createObjectsCache((...args) => new Service(...args), '__' + name);
            Object.defineProperty(Service, '_cache', {
                value: _cache,
                writable: false,
                enumerable: false,
            });
            Object.defineProperty(Service, 'createInstance', {
                value: _cache.get,
                writable: false,
            });
        }
        else {
            Object.defineProperty(Service, 'getInstance', {
                value: createSingleton(() => new Service(), '__' + name),
                writable: false,
            });
        }
        //                                                  
    }
    return Service;
}
export default createService;
